import { DecoupledEditor } from "ckeditor5";
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from "react";

import ToolbarFocusManager from "../util/ToolbarFocusManager";

const ToolbarFocusManagerContext = createContext<ToolbarFocusManager | null>(
  null,
);

/**
 * Provider for the seamless RCE's shared toolbar, used to establish a new
 * scope for shared toolbars.
 */
export const SharedToolbarProvider = (props: PropsWithChildren) => {
  const focusManager = useMemo(() => new ToolbarFocusManager(), []);

  return (
    <ToolbarFocusManagerContext.Provider value={focusManager}>
      {props.children}
    </ToolbarFocusManagerContext.Provider>
  );
};

/**
 * Container for the seamless RCE's shared toolbar. You should only have one of these
 * within a given shared toolbar scope. A new scope can be established by wrapping a component
 * in a SharedToolbarProvider.
 */
export const SharedToolbarContainer = (
  props: Omit<React.HTMLAttributes<HTMLDivElement>, "ref">,
) => {
  const context = useContext(ToolbarFocusManagerContext);
  const toolbarRef = useCallback(
    (element: HTMLElement | null) => {
      if (element == null) return;
      context?.setContainer(element);
    },
    [context],
  );

  return <div {...props} ref={toolbarRef} />;
};

export const useSharedToolbar = () => {
  const context = useContext(ToolbarFocusManagerContext);
  return useMemo(
    () => ({
      registerEditor(editor: DecoupledEditor) {
        if (context == null) {
          console.warn(
            `Seamless editor used without a toolbar context. ` +
              `Please ensure you have a <ToolbarProvider> element somewhere above editor '${editor.id}'.`,
          );

          return;
        }

        context.registerEditor(editor);
      },
    }),
    [context],
  );
};
